import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Check, Zap, Home, ChevronDown } from "lucide-react";
import { MaskedLine, Reveal } from "./Reveal";
import { HERO_BG, HERO_VIDEO, PHONE_DISPLAY, PHONE_TEL } from "@/constants/site";
import { scrollToTarget } from "@/lib/scroll";

const CHECKLIST = [
    "Toutes assurances",
    "Aucune avance de frais",
    "Garantie à vie sur la pose et l'étanchéité",
    "Intervention à domicile ou sur votre lieu de travail",
];

export const Hero = () => {
    const { scrollY } = useScroll();
    const bgY = useTransform(scrollY, [0, 900], [0, 220]);
    const bgScale = useTransform(scrollY, [0, 900], [1.05, 1.22]);
    const fade = useTransform(scrollY, [0, 600], [1, 0.15]);

    return (
        <section id="accueil" data-testid="hero-section" className="relative min-h-screen flex items-center overflow-hidden">
            <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0 will-change-transform">
                <video
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    poster={HERO_BG}
                    aria-hidden="true"
                    className="w-full h-full object-cover"
                >
                    <source src={HERO_VIDEO} type="video/mp4" />
                </video>
            </motion.div>
            <div className="absolute inset-0 bg-gradient-to-r from-[#0A0E17] via-[#0A0E17]/80 to-[#0A0E17]/30" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0E17] via-transparent to-[#0A0E17]/50" />

            <motion.div style={{ opacity: fade }} className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-32 pb-44 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center">
                <div className="min-w-0">
                    <Reveal delay={0.15}>
                        <span className="flex items-center gap-3 text-xs sm:text-sm font-bold tracking-[0.15em] sm:tracking-[0.25em] uppercase text-gold">
                            <span className="h-px w-10 bg-gold/70 shrink-0" />
                            <span className="min-w-0">Spécialiste du remplacement de pare-brise</span>
                        </span>
                    </Reveal>

                    <h1 className="mt-6 font-display font-black uppercase leading-[0.95] tracking-tight">
                        <MaskedLine delay={0.3} className="text-4xl min-[400px]:text-5xl sm:text-7xl xl:text-8xl text-white">
                            Pare-Brise
                        </MaskedLine>
                        <MaskedLine delay={0.45} className="text-4xl min-[400px]:text-5xl sm:text-7xl xl:text-8xl">
                            <span className="text-gold-gradient gold-shimmer">Paris</span>
                        </MaskedLine>
                    </h1>

                    <MaskedLine delay={0.6} className="mt-6 text-base md:text-lg text-gray-300 font-light">
                        Rapide, fiable et 100% pris en charge
                    </MaskedLine>
                    <MaskedLine delay={0.7} className="mt-1 text-lg md:text-xl font-bold uppercase tracking-wide text-gold-light">
                        Aucune avance de frais
                    </MaskedLine>

                    <ul className="mt-8 space-y-3">
                        {CHECKLIST.map((item, i) => (
                            <motion.li
                                key={item}
                                data-testid={`hero-check-${i}`}
                                initial={{ opacity: 0, x: -24 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.85 + i * 0.12, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                                className="flex items-center gap-3 text-sm sm:text-base text-gray-200"
                            >
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-gold/15 border border-gold/50 text-gold shrink-0">
                                    <Check size={13} strokeWidth={3} />
                                </span>
                                {item}
                            </motion.li>
                        ))}
                    </ul>

                    <motion.div
                        initial={{ opacity: 0, y: 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 1.4, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                        className="mt-10 flex flex-wrap items-center gap-4"
                    >
                        <motion.a
                            data-testid="hero-phone-cta"
                            href={PHONE_TEL}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-2.5 rounded-lg bg-gradient-to-r from-gold-dark via-gold to-gold-light px-8 py-4 font-bold uppercase tracking-wide text-sm text-midnight-900 shadow-[0_0_40px_rgba(212,175,55,0.4)]"
                        >
                            <Phone size={19} strokeWidth={2.5} />
                            {PHONE_DISPLAY}
                        </motion.a>
                        <motion.a
                            data-testid="hero-number-cta"
                            href={PHONE_TEL}
                            whileHover={{ scale: 1.05, borderColor: "rgba(212,175,55,0.9)" }}
                            whileTap={{ scale: 0.95 }}
                            className="inline-flex items-center gap-2.5 rounded-lg border border-gold/50 px-8 py-4 font-bold uppercase tracking-wide text-sm text-gold-light transition-colors duration-300 hover:bg-gold/10"
                        >
                            Appelez-nous
                        </motion.a>
                    </motion.div>
                </div>

                <div className="relative flex flex-col items-center lg:items-end gap-10 mt-14 lg:mt-24">
                    <motion.div
                        data-testid="promo-badge"
                        initial={{ opacity: 0, y: 40, rotate: 2 }}
                        animate={{ opacity: 1, y: 0, rotate: 0 }}
                        transition={{ delay: 1.1, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="relative bg-white text-midnight-900 rounded-2xl px-8 pt-9 pb-6 w-64 text-center shadow-[0_25px_60px_rgba(0,0,0,0.5)] border-2 border-gold"
                        >
                            <span className="absolute -top-3.5 left-1/2 -translate-x-1/2 rounded-md bg-gradient-to-r from-gold-dark via-gold to-gold-light px-4 py-1 text-[11px] font-bold tracking-[0.2em] uppercase text-midnight-900">
                                Nouveau
                            </span>
                            <span className="block text-sm font-bold tracking-widest uppercase text-midnight-800">
                                Jusqu'à
                            </span>
                            <span className="block font-display font-black text-6xl leading-none my-1 text-gold-dark">
                                450&nbsp;€
                            </span>
                            <span className="block text-sm font-bold tracking-widest uppercase text-midnight-800">
                                offerts
                            </span>
                            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-gold-dark border-t border-gold/40 pt-3">
                                <Zap size={13} fill="currentColor" />
                                En virement instantané
                            </span>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        data-testid="home-intervention-badge"
                        initial={{ opacity: 0, scale: 0.7 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 1.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                    >
                        <motion.div
                            animate={{ y: [0, 10, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                            className="w-44 h-44 rounded-full border-2 border-gold bg-[#0A0E17]/70 backdrop-blur-md flex flex-col items-center justify-center text-center gap-1.5 shadow-[0_0_50px_rgba(212,175,55,0.25)]"
                        >
                            <Home size={22} className="text-gold" />
                            <span className="text-[13px] font-bold tracking-wider uppercase text-white leading-tight">
                                Intervention<br />à domicile
                            </span>
                            <span className="text-[10px] text-gray-300 leading-snug px-4">
                                ou sur votre lieu de travail, partout en France
                            </span>
                        </motion.div>
                    </motion.div>
                </div>
            </motion.div>

            <motion.button
                data-testid="scroll-indicator"
                onClick={() => scrollToTarget("#services")}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                className="absolute bottom-7 left-1/2 -translate-x-1/2 z-10 text-gold/80 hover:text-gold transition-colors"
                aria-label="Défiler vers les services"
            >
                <motion.span
                    animate={{ y: [0, 8, 0] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                    className="block"
                >
                    <ChevronDown size={28} />
                </motion.span>
            </motion.button>
        </section>
    );
};
